import { useState, useEffect, useRef } from 'react';
import AevaraLogo from './AevaraLogo';
import Icon from './Icon';
import { COLORS, FONTS, TRACKING } from '../styles/tokens';
import { primaryButton, contentWrap, kicker } from '../styles/shared';
import { track } from '../lib/analytics';
import { generateReportDoc, reportBase64, downloadReport } from '../lib/report';
import { emailReport } from '../lib/api';

// Delivery step states, in the order they resolve.
const STEP = { pending: 'pending', active: 'active', done: 'done' };

// How long a send may run before we offer a way past this screen.
const SLOW_MS = 10000;

// The interstitial between the gate form and the results page: it builds the
// PDF report, emails it to the address the user just gave us, shows that
// happening, and only then hands them through to the on-screen report.
//
// The email send lives here rather than on the results page so the user is
// never told "check your inbox" before we know whether anything was sent — and
// so the built PDF can be handed forward instead of being rendered twice.
export default function SendingScreen({ answers, gateData, assessmentId, onView }) {
  // 'building' → generating the PDF · 'emailing' → handing it to the server ·
  // 'done' → finished, outcome is in `status`
  const [phase, setPhase] = useState('building');
  // null while in flight · 'sent' — Brevo accepted it · 'unsent' — nothing went
  // out (send failed, was skipped server-side, or the lead never saved)
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // A slow send (the email request allows up to 20s) must never trap anyone on
  // this screen, so after SLOW_MS we offer a way through while it finishes.
  const [slow, setSlow] = useState(false);
  // True if the PDF itself couldn't be rendered — there is then nothing to email
  // and nothing to download, but the on-screen report is unaffected.
  const [buildFailed, setBuildFailed] = useState(false);

  // The built document, kept so the download button and the results page can
  // reuse it instead of re-rendering the whole report.
  const docRef = useRef(null);
  // Guards StrictMode's dev double-invoke so the report is emailed only once.
  const startedRef = useRef(false);

  useEffect(() => {
    track('report_delivery_shown', { emailable: !!assessmentId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Arms the escape hatch once a send has been running for a while.
  useEffect(() => {
    if (phase !== 'emailing') return;
    const t = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Hands the PDF bytes to the server, which emails them via Brevo.
  async function send(doc) {
    setSlow(false);
    setPhase('emailing');
    try {
      const res = await emailReport(assessmentId, reportBase64(doc));
      setStatus(res.status === 'sent' ? 'sent' : 'unsent');
      track('report_email_result', { status: res.status });
    } catch (err) {
      console.error('[Aevara Assessment] report email send failed:', err);
      setStatus('unsent');
      track('report_email_result', { status: 'failed' });
    }
    setPhase('done');
  }

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        docRef.current = await generateReportDoc(answers, gateData);
      } catch (err) {
        // The report couldn't be rendered — send nothing and let them through
        // to the on-screen results, which don't depend on the PDF.
        console.error('[Aevara Assessment] report build failed:', err);
        setBuildFailed(true);
        setStatus('unsent');
        setPhase('done');
        return;
      }
      // No assessmentId means the lead never saved, so there's nothing to email
      // against. The report itself is fine — show it.
      if (!assessmentId) {
        setStatus('unsent');
        setPhase('done');
        return;
      }
      await send(docRef.current);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      const doc = docRef.current || await generateReportDoc(answers, gateData);
      docRef.current = doc;
      downloadReport(doc, gateData.firstName);
      track('report_downloaded', { source: 'delivery' });
    } catch (err) {
      console.error('[Aevara Assessment] report download failed:', err);
    } finally {
      setDownloading(false);
    }
  }

  // Re-attempts the send, rebuilding the PDF first if that's what failed.
  async function handleRetry() {
    setStatus(null);
    setPhase('building');
    try {
      if (!docRef.current) docRef.current = await generateReportDoc(answers, gateData);
      setBuildFailed(false);
    } catch (err) {
      console.error('[Aevara Assessment] report build failed:', err);
      setBuildFailed(true);
      setStatus('unsent');
      setPhase('done');
      return;
    }
    await send(docRef.current);
  }

  const working = phase !== 'done';
  const sent    = status === 'sent';

  const buildStep = phase === 'building' ? STEP.active : STEP.done;
  const emailStep = phase === 'building'
    ? STEP.pending
    : phase === 'emailing'
      ? STEP.active
      : STEP.done;

  const headline = working
    ? 'Sending your report…'
    : sent
      ? `Your report is on its way, ${gateData.firstName}.`
      : 'Your report is ready.';

  const body = working
    ? `We're building your personalized AI readiness report and emailing it to ${gateData.email}.`
    : sent
      ? `We've emailed your full PDF report to ${gateData.email}. If it isn't there in a minute, check your spam folder.`
      : buildFailed
        ? "We couldn't put the PDF together this time — but your full report is ready to read on screen."
        : "We couldn't email your report this time. You can still read it on screen right now, or download the PDF.";

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bgApp, padding: '0 20px', boxSizing: 'border-box' }}>
      <main id="main-content" style={{ ...contentWrap, maxWidth: 600, padding: '40px 20px 80px' }}>

        {/* Logo */}
        <div style={{ marginBottom: 40 }}>
          <AevaraLogo variant="light" height={32} />
        </div>

        <div className="aev-fade-in" style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: '32px 32px 28px',
        }}>

          {/* Status badge + icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: sent ? 'rgba(42,192,208,0.10)' : 'rgba(37,99,255,0.08)',
              border: `1px solid ${sent ? 'rgba(42,192,208,0.30)' : 'rgba(37,99,255,0.24)'}`,
              flexShrink: 0,
            }}>
              {working
                ? <Spinner size={20} color={COLORS.accent} />
                : <Icon
                    name={sent ? 'mark_email_read' : 'description'}
                    size={22}
                    style={{ color: sent ? COLORS.holidayBlue : COLORS.accent }}
                  />}
            </span>
            <p style={{
              ...kicker,
              margin: 0,
              color: COLORS.textMuted,
            }}>
              {working ? 'Delivering your report' : sent ? 'Report sent' : 'Report ready'}
            </p>
          </div>

          {/* Headline + body — announced as it resolves */}
          <div role="status" aria-live="polite">
            <h1 style={{
              fontFamily: FONTS.body,
              fontSize: 'clamp(22px, 3.4vw, 27px)',
              fontWeight: 500,
              color: COLORS.textPrimary,
              letterSpacing: TRACKING.tightHeadline,
              lineHeight: 1.3,
              margin: '0 0 10px',
            }}>
              {headline}
            </h1>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 16,
              lineHeight: 1.65,
              color: COLORS.textSecondary,
              margin: 0,
            }}>
              {body}
            </p>
          </div>

          {/* Delivery steps */}
          <div style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}>
            <Step
              state={buildStep}
              label="Building your personalized report"
              failed={buildFailed}
            />
            <Step
              state={emailStep}
              label={sent || working ? `Emailing it to ${gateData.email}` : 'Emailing your report'}
              failed={phase === 'done' && !sent}
            />
          </div>

          {/* Actions */}
          <div style={{ marginTop: 28 }}>
            <button
              type="button"
              onClick={() => onView(docRef.current, status === 'sent' ? 'sent' : 'unsent')}
              disabled={working}
              aria-busy={working}
              style={{
                ...primaryButton,
                width: '100%',
                gap: 8,
                opacity: working ? 0.55 : 1,
                cursor: working ? 'not-allowed' : 'pointer',
                boxShadow: working ? 'none' : primaryButton.boxShadow,
              }}
            >
              {working ? 'Preparing your results…' : 'View my report'}
              {!working && <Icon name="arrow_forward" size={18} />}
            </button>

            {/* Escape hatch for a slow send — the email keeps going in the
                background, so the results page says so rather than claiming
                the report has landed. */}
            {working && slow && (
              <p style={{ textAlign: 'center', margin: '16px 0 0' }}>
                <button
                  type="button"
                  onClick={() => onView(docRef.current, 'sending')}
                  className="btn-ghost"
                  style={linkButton(false)}
                >
                  Taking a while — view my report now
                </button>
              </p>
            )}

            {/* Secondary actions appear only once there's something to act on */}
            {!working && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16,
                marginTop: 16,
              }}>
                {!buildFailed && (
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={downloading}
                    className="btn-ghost"
                    style={linkButton(downloading)}
                  >
                    {downloading ? 'Preparing PDF…' : 'Download the PDF'}
                  </button>
                )}
                {!sent && assessmentId && (
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="btn-ghost"
                    style={linkButton(false)}
                  >
                    Try emailing it again
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
}

const linkButton = (busy) => ({
  background: 'none',
  border: 'none',
  padding: 0,
  fontFamily: FONTS.body,
  fontSize: 15,
  fontWeight: 600,
  color: COLORS.holidayBlue,
  cursor: busy ? 'not-allowed' : 'pointer',
  opacity: busy ? 0.6 : 1,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
});

// ── Delivery step row ───────────────────────────────────────────────────────

function Step({ state, label, failed }) {
  const done   = state === STEP.done;
  const active = state === STEP.active;
  const color  = failed
    ? COLORS.textMuted
    : done
      ? COLORS.holidayBlue
      : active
        ? COLORS.textPrimary
        : COLORS.textMuted;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        flexShrink: 0,
      }}>
        {active
          ? <Spinner size={16} color={COLORS.accent} />
          : <Icon
              name={failed ? 'error' : done ? 'check_circle' : 'radio_button_unchecked'}
              size={18}
              style={{ color: failed ? '#F87171' : done ? COLORS.holidayBlue : 'rgba(249,250,251,0.22)' }}
            />}
      </span>
      <span style={{
        fontFamily: FONTS.body,
        fontSize: 15,
        fontWeight: active || done ? 500 : 400,
        color,
        lineHeight: 1.4,
        wordBreak: 'break-word',
      }}>
        {label}
      </span>
    </div>
  );
}

// ── Spinner ─────────────────────────────────────────────────────────────────
// Ring with a single lit quadrant. Rotation is CSS (.aev-spin), which is
// disabled under prefers-reduced-motion — the ring stays visible either way.

function Spinner({ size = 20, color }) {
  return (
    <span
      className="aev-spin"
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        border: `2px solid rgba(249,250,251,0.14)`,
        borderTopColor: color,
        boxSizing: 'border-box',
      }}
    />
  );
}
